import { PublicClientApplication, AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "./authConfig";

export interface UserInfo {
  id: string;
  displayName: string;
  email: string;
  accessToken: string;
}

class AuthService {
  private msalInstance: PublicClientApplication;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  /**
   * Initialize MSAL instance
   */
  async initialize(): Promise<void> {
    await this.msalInstance.initialize();
  }

  /**
   * Get all accounts
   */
  getAllAccounts(): AccountInfo[] {
    return this.msalInstance.getAllAccounts();
  }

  /**
   * Get the current signed-in account
   */
  getActiveAccount(): AccountInfo | null {
    const accounts = this.getAllAccounts();
    if (accounts.length === 0) {
      return null;
    }
    
    return this.msalInstance.getActiveAccount() || accounts[0];
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const account = this.getActiveAccount();
    return account !== null;
  }

  /**
   * Sign in user with popup
   */
  async signInPopup(): Promise<UserInfo> {
    try {
      // First, check if we can handle redirect result
      const redirectResult = await this.msalInstance.handleRedirectPromise();
      if (redirectResult && redirectResult.account) {
        this.msalInstance.setActiveAccount(redirectResult.account);
        return this.extractUserInfo(redirectResult);
      }

      // Configure popup request for Office add-ins
      const popupRequest = {
        ...loginRequest,
        redirectUri: window.location.origin,
        // Office add-ins specific configuration
        extraQueryParameters: {
          response_mode: 'fragment'
        }
      };

      const loginResponse: AuthenticationResult = await this.msalInstance.loginPopup(popupRequest);
      
      if (loginResponse.account) {
        this.msalInstance.setActiveAccount(loginResponse.account);
      }

      return this.extractUserInfo(loginResponse);
    } catch (error) {
      console.error("Login failed:", error);
      // Check if it's the SPA configuration error
      if (error.errorCode === "invalid_request" && error.errorMessage?.includes("Cross-origin token redemption")) {
        throw new Error("Azure AD application must be configured as Single-Page Application (SPA). Please update your app registration in Azure Portal. See AZURE_AD_FIX.md for instructions.");
      }
      throw error;
    }
  }

  /**
   * Alternative sign in using redirect (fallback for popup issues)
   */
  async signInRedirect(): Promise<void> {
    try {
      const redirectRequest = {
        ...loginRequest,
        redirectUri: window.location.origin,
      };

      await this.msalInstance.loginRedirect(redirectRequest);
    } catch (error) {
      console.error("Redirect login failed:", error);
      throw error;
    }
  }

  /**
   * Sign in user silently (if already authenticated)
   */
  async signInSilent(): Promise<UserInfo | null> {
    const account = this.getActiveAccount();
    
    if (!account) {
      return null;
    }

    try {
      const silentRequest = {
        ...loginRequest,
        account: account,
      };

      const response = await this.msalInstance.acquireTokenSilent(silentRequest);
      return this.extractUserInfo(response);
    } catch (error) {
      console.error("Silent login failed:", error);
      return null;
    }
  }

  /**
   * Get access token silently
   */
  async getAccessToken(): Promise<string | null> {
    const account = this.getActiveAccount();
    
    if (!account) {
      return null;
    }

    try {
      const silentRequest = {
        ...loginRequest,
        account: account,
      };

      const response = await this.msalInstance.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch (error) {
      console.error("Failed to get access token:", error);
      return null;
    }
  }

  /**
   * Sign out user
   */
  async signOut(): Promise<void> {
    const account = this.getActiveAccount();
    
    if (account) {
      await this.msalInstance.logoutPopup({
        account: account,
      });
    }
  }

  /**
   * Extract user information from authentication result
   */
  private extractUserInfo(authResult: AuthenticationResult): UserInfo {
    const { account, accessToken } = authResult;
    
    if (!account) {
      throw new Error("No account found in authentication result");
    }

    return {
      id: account.localAccountId || account.homeAccountId,
      displayName: account.name || "Unknown User",
      email: account.username || "unknown@email.com",
      accessToken: accessToken,
    };
  }

  /**
   * Handle authentication errors
   */
  handleAuthError(error: any): string {
    if (error.errorCode === "user_cancelled") {
      return "Authentication was cancelled by the user.";
    } else if (error.errorCode === "popup_window_error") {
      return "Popup window was blocked. Please allow popups and try again.";
    } else if (error.errorCode === "network_error") {
      return "Network error occurred. Please check your connection and try again.";
    } else if (error.errorCode === "invalid_request" && error.errorMessage?.includes("Cross-origin token redemption")) {
      return "Configuration Error: The Azure AD application must be registered as a Single-Page Application (SPA).";
    } else if (error.errorMessage?.includes("AADSTS9002326")) {
      return "Azure AD Configuration Error: Please configure your app as Single-Page Application in Azure Portal.";
    } else if (error.errorMessage?.includes("AADSTS50020")) {
      return "This account cannot access the app in the current tenant. If the app is multi-tenant, grant tenant consent and sign in again.";
    } else if (error.errorMessage?.includes("AADSTS65001")) {
      return "Your organization has not granted consent for this app yet. Ask your Microsoft 365 admin to approve the required permissions.";
    } else {
      return `Authentication failed: ${error.errorMessage || error.message || "Please try again."}`;
    }
  }
}

// Create and export a singleton instance
export const authService = new AuthService();
export default authService;
